// Trade Calculator — pure functions, parameterized fee rate
// Ported from HoneyBadger's 1% Breakeven Strategy calculator

export const calcFee = (grossAmount, feeRate) => grossAmount * feeRate;

export const calcNetAmount = (grossAmount, action, feeRate) => {
  const fee = calcFee(grossAmount, feeRate);
  return action === 'BUY' ? grossAmount + fee : grossAmount - fee;
};

// BUY-side: buy BTC, sell later at higher price (profit in fiat)
export const calcBreakevenSell = (buyPrice, feeRate) =>
  buyPrice * (1 + feeRate) / (1 - feeRate);

export const calcTargetSell = (buyPrice, gainPct, feeRate) =>
  (buyPrice * (1 + feeRate) * (1 + gainPct / 100)) / (1 - feeRate);

export const calcFiatGained = (buyPrice, sellPrice, btcAmount, feeRate) => {
  const grossBuyCost = buyPrice * btcAmount;
  const totalBuyCost = grossBuyCost + calcFee(grossBuyCost, feeRate);
  const grossSellProceeds = sellPrice * btcAmount;
  const netSellProceeds = grossSellProceeds - calcFee(grossSellProceeds, feeRate);
  return netSellProceeds - totalBuyCost;
};

export const calcBuyStopLoss = (buyPrice, stopLossPct) =>
  buyPrice * (1 - stopLossPct / 100);

// SELL-side: sell BTC, buy back lower (profit in BTC)
export const calcBreakevenBuyback = (sellPrice, feeRate) =>
  sellPrice * (1 - feeRate * 2);

export const calcTargetBuyback = (sellPrice, btcGainPct, feeRate) =>
  (sellPrice * (1 - feeRate)) / ((1 + btcGainPct / 100) * (1 + feeRate));

export const calcBtcGained = (sellPrice, buybackPrice, btcAmount, feeRate) => {
  const grossSellAmount = sellPrice * btcAmount;
  const netSellAmount = grossSellAmount - calcFee(grossSellAmount, feeRate);
  const resultBtc = netSellAmount / (buybackPrice * (1 + feeRate));
  return resultBtc - btcAmount;
};

export const calcSellStopLoss = (sellPrice, stopLossPct) =>
  sellPrice * (1 + stopLossPct / 100);

// Back-calculate net gain % from a target exit price
export const calcNetGainPct = (action, entryPrice, exitPrice, feeRate) => {
  if (action === 'BUY') {
    // BUY: paid entryPrice + fee, selling at exitPrice - fee
    const costPerBtc = entryPrice * (1 + feeRate);
    const proceedsPerBtc = exitPrice * (1 - feeRate);
    return ((proceedsPerBtc / costPerBtc) - 1) * 100;
  }
  // SELL: received entryPrice - fee, rebuying at exitPrice + fee
  const netSellPerBtc = entryPrice * (1 - feeRate);
  const costToBuyBack = exitPrice * (1 + feeRate);
  return ((netSellPerBtc / costToBuyBack) - 1) * 100;
};

// Master preview — returns all computed values for a proposed trade
export const buildTradePreview = (action, price, btcAmount, targetPct, stopLossPct, feeRate) => {
  const grossAmount = price * btcAmount;
  const fee = calcFee(grossAmount, feeRate);
  const netAmount = calcNetAmount(grossAmount, action, feeRate);

  if (action === 'SELL') {
    const breakevenPrice = calcBreakevenBuyback(price, feeRate);
    const targetPrice = targetPct !== 0 ? calcTargetBuyback(price, targetPct, feeRate) : null;
    const gainAtTarget = targetPrice ? calcBtcGained(price, targetPrice, btcAmount, feeRate) : null;
    const stopLossPrice = stopLossPct > 0 ? calcSellStopLoss(price, stopLossPct) : null;
    return { grossAmount, fee, netAmount, breakevenPrice, targetPrice, gainAtTarget, stopLossPrice };
  }

  const breakevenPrice = calcBreakevenSell(price, feeRate);
  const targetPrice = targetPct !== 0 ? calcTargetSell(price, targetPct, feeRate) : null;
  const gainAtTarget = targetPrice ? calcFiatGained(price, targetPrice, btcAmount, feeRate) : null;
  const stopLossPrice = stopLossPct > 0 ? calcBuyStopLoss(price, stopLossPct) : null;
  return { grossAmount, fee, netAmount, breakevenPrice, targetPrice, gainAtTarget, stopLossPrice };
};
